import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { forceRound } from "helpers/function";
import { NUMBER_ELEMENT_PER_PAGE } from "constants/dataTable";
import { ToastBottomEnd } from "views/components/Toast";
import {
  toastDeleteErrorMessageConfig,
  toastDeleteSuccessMessageConfig,
  toastErrorMessageWithParameterConfig,
} from "config/toastConfig";
import { verifyTokenExpire } from "middleware/verifyToken";
import { useTranslation } from "react-i18next";

export const useClients = (
  endPoint,
  initialNumberPerPage = NUMBER_ELEMENT_PER_PAGE
) => {
  const { t } = useTranslation();

  const [state, setState] = useState({
    loading: true,
    clients: [],
    showList: [],
    pagination: {
      total: 0,
      currentPage: 1,
      numberPerPage: initialNumberPerPage,
      totalPages: 0,
      nextUrl: null,
      prevUrl: null,
    },
    error: null,
  });

  const fetchClients = useCallback(
    async (page = 1, searchKey = "", numberPerPage = null) => {
      if (!verifyTokenExpire()) return;

      setState((prev) => ({ ...prev, loading: true }));

      try {
        // Use the provided numberPerPage, or the current state value if not provided
        const perPage = numberPerPage !== null && numberPerPage !== undefined ? numberPerPage : state.pagination.numberPerPage;
        
        const url = searchKey
          ? `${endPoint.list}?page=${page}&size=${perPage}&key=${searchKey}`
          : `${endPoint.list}?page=${page}&size=${perPage}`;

        const response = await axios.get(url);

        setState((prev) => ({
          ...prev,
          loading: false,
          clients: response.data.data,
          showList: response.data.data.slice(
            0,
            response.data.per_page || perPage
          ),
          pagination: {
            ...prev.pagination,
            total: response.data.total,
            currentPage: page,
            numberPerPage: perPage,
            totalPages: forceRound(
              response.data.total /
                (response.data.per_page || perPage)
            ),
            nextUrl: response.data.next_page_url,
            prevUrl: response.data.prev_page_url,
          },
          error: null,
        }));
      } catch (error) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error:
            error.response?.data?.message ||
            t("Erreur lors du chargement des données"),
        }));
      }
    },
    [endPoint, t]
  );

  const searchClients = useCallback(
    (searchTerm) => {
      fetchClients(1, searchTerm, state.pagination.numberPerPage);
    },
    [fetchClients, state.pagination.numberPerPage]
  );

  const handlePageChange = useCallback(
    (page) => {
      fetchClients(page, "", state.pagination.numberPerPage);
    },
    [fetchClients, state.pagination.numberPerPage]
  );

  const handleItemsPerPageChange = useCallback(
    (event) => {
      const newPerPage = parseInt(event.target.value);
      
      // Update state first, then fetch data
      setState((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          numberPerPage: newPerPage,
          currentPage: 1, // Reset to first page
        },
      }));
      
      // Fetch data with the new numberPerPage value
      fetchClients(1, "", newPerPage);
    },
    [fetchClients]
  );

  const handleNextPage = useCallback(() => {
    if (
      state.pagination.currentPage < state.pagination.totalPages &&
      state.pagination.nextUrl
    ) {
      handlePageChange(state.pagination.currentPage + 1);
    }
  }, [handlePageChange, state.pagination]);

  const handlePreviousPage = useCallback(() => {
    if (state.pagination.currentPage > 1 && state.pagination.prevUrl) {
      handlePageChange(state.pagination.currentPage - 1);
    }
  }, [handlePageChange, state.pagination]);

  const deleteClient = useCallback(
    async (accountId, index) => {
      if (!verifyTokenExpire()) return;

      try {
        await axios.delete(endPoint.destroy(accountId));

        if (state.showList.length > 1) {
          fetchClients(state.pagination.currentPage, "", state.pagination.numberPerPage);
        } else {
          fetchClients(Math.max(state.pagination.currentPage - 1, 1), "", state.pagination.numberPerPage);
        }

        ToastBottomEnd.fire(toastDeleteSuccessMessageConfig());
      } catch (error) {
        if (error.response?.data?.error) {
          ToastBottomEnd.fire(
            toastErrorMessageWithParameterConfig(error.response.data.error)
          );
        } else {
          ToastBottomEnd.fire(toastDeleteErrorMessageConfig());
        }
      }
    },
    [
      endPoint,
      fetchClients,
      state.pagination.currentPage,
      state.showList.length,
    ]
  );

  useEffect(() => {
    if (endPoint && endPoint.list) {
      fetchClients(1, "", initialNumberPerPage);
    }
  }, [endPoint, fetchClients, initialNumberPerPage]);

  return {
    ...state,
    fetchClients,
    searchClients,
    deleteClient,
    handlePageChange,
    handleNextPage,
    handlePreviousPage,
    handleItemsPerPageChange,
  };
};
